import { useGetUserQuery } from '@/api/piloteer';
import { useAppSelector } from '@/store/hooks';
import { UserRole } from '@/types/user';
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

interface RoleBasedRouteProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallback?: string;
}

export const RoleBasedRoute = ({
  children,
  allowedRoles,
  fallback = '/dashboard',
}: RoleBasedRouteProps) => {
  const { session } = useAppSelector((state) => state.auth);

  // Get user data from backend API
  const {
    data: backendUser,
    isLoading,
    error,
  } = useGetUserQuery(undefined, {
    skip: !session, // Only fetch when we have a session
  });

  // If no session, redirect to login
  if (!session) {
    return <Navigate to='/auth/login' replace />;
  }

  // If still loading backend user data, show loading (could use a spinner here)
  if (isLoading) {
    // We could return a loading spinner here, but for now just wait
    return null;
  }

  // Debug logging
  console.log('RoleBasedRoute Debug:', {
    session: !!session,
    backendUser,
    userRole: backendUser?.role,
    allowedRoles,
    isAllowed: backendUser?.role
      ? allowedRoles.includes(backendUser.role)
      : false,
    isLoading,
    error,
  });

  // If error fetching user or no user role, redirect to login
  if (error || !backendUser || !backendUser.role) {
    return <Navigate to='/auth/login' replace />;
  }

  // Check if user role is allowed
  if (!allowedRoles.includes(backendUser.role)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

// Performance Partner access is a flag on top of the user's normal role.
export const PerformancePartnerRoute = ({
  children,
  fallback = '/dashboard',
}: {
  children: ReactNode;
  fallback?: string;
}) => {
  const { session } = useAppSelector((state) => state.auth);
  const { data: backendUser, isLoading } = useGetUserQuery(undefined, {
    skip: !session,
  });

  if (!session) return <Navigate to='/auth/login' replace />;
  if (isLoading) return null;
  if (!backendUser) return <Navigate to='/auth/login' replace />;
  if (!backendUser.isPerformancePartner) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
};
